/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import clsx from "clsx"
import { observer } from "mobx-react-lite"

import { deviceName } from "../../features/application/device-name"
import type { ApplicationDetail } from "../../features/application-detail/application-detail-types"
import { valueOrDash } from "../../utils/value-or-dash"
import { ClipboardValue } from "../components/clipboard-value"
import { RiskScore } from "../components/risk-score"
import { TimestampWithIcon } from "../components/timestamp-with-icon"
import { Card, CardAction, CardContent, CardFooter, CardHeader, CardTitle } from "../primitives/card"
import styles from "./application-detail-info.module.css"
import { RiskFlag } from "./risk-flag"

interface Props {
    /**
     * Device returned by the detail endpoint.
     *
     * The list data is rendered first when navigating from the device list; the
     * detail response then replaces it with the complete device, including flags.
     */
    detail: ApplicationDetail
}

/**
 * Displays the device summary at the top of the detail page.
 *
 * Shows the device identity, risk score, current flags, identifiers,
 * application details, and timestamps. Previous flags are shown separately in
 * the flag history tab.
 *
 * Key/value details use description lists so assistive technology treats them
 * as labelled values rather than table cells.
 *
 * @example
 * {vm.applicationDetail && <ApplicationDetailInfo detail={vm.applicationDetail} />}
 */
export const ApplicationDetailInfo = observer(({ detail }: Props) => (
    <Card className="gap-0 max-inline-2xl">
        <CardHeader className={styles.header}>
            <CardTitle>
                <h2 className="m-0 text-3xl font-semibold">
                    <span className="pe-2 font-normal">{deviceName(detail).brand}</span>
                    <span className="wrap-anywhere">{deviceName(detail).model}</span>
                </h2>
            </CardTitle>
            <CardAction>
                <RiskScore score={detail.riskScore} />
            </CardAction>
            {/* Omit the list when the device has no current flags. */}
            {!!detail.flags?.length && (
                <ul className={styles.flags} aria-label="Flags">
                    {detail.flags.map((flag) => (
                        <li key={flag.name}>
                            <RiskFlag name={flag.name} score={flag.score} />
                        </li>
                    ))}
                </ul>
            )}
        </CardHeader>
        <CardContent className={styles.body}>
            <dl className={styles.table}>
                <dt className={styles.key}>Client User ID:</dt>
                <dd className={clsx("font-mono", styles.value)}>
                    <ClipboardValue value={detail.clientUserId} />
                </dd>

                <dt className={styles.key}>Client Device ID:</dt>
                <dd className={clsx("font-mono", styles.value)}>
                    <ClipboardValue value={detail.clientDeviceId} />
                </dd>

                <dt className={styles.key}>Internal Device ID:</dt>
                <dd className={clsx("font-mono", styles.value)}>
                    <ClipboardValue value={detail.avUserId} />
                </dd>

                <dt className={styles.key}>Application:</dt>
                <dd className={clsx("font-mono", styles.value)}>{valueOrDash(detail.description)}</dd>

                <dt className={styles.key}>Application Package:</dt>
                <dd className={clsx("font-mono", styles.value)}>{valueOrDash(detail.appPackageName)}</dd>
            </dl>
        </CardContent>
        <CardFooter className={styles.footer}>
            <dl className={styles.timestamps}>
                <div>
                    <dt className={styles.key}>First Seen:</dt>
                    <dd className={styles.value}>
                        <TimestampWithIcon kind="created" timestamp={detail.timestampCreated} />
                    </dd>
                </div>

                {/* Omit Updated when the device has not changed since it was first seen. */}
                {detail.timestampUpdated && (
                    <div>
                        <dt className={styles.key}>Updated:</dt>
                        <dd className={styles.value}>
                            <TimestampWithIcon kind="updated" timestamp={detail.timestampUpdated} />
                        </dd>
                    </div>
                )}

                <div>
                    <dt className={styles.key}>Last Seen:</dt>
                    <dd className={styles.value}>
                        <TimestampWithIcon kind="lastSeen" timestamp={detail.timestampLastSeen} />
                    </dd>
                </div>
            </dl>
        </CardFooter>
    </Card>
))
